import { useState } from 'react'
import { router, Link, useForm } from '@inertiajs/react'
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Text,
  Badge,
  EmptyState,
  Modal,
  TextField,
  ComboBox,
  PhoneIcon,
} from '@/components/design-system'

interface WabaRow {
  id: string
  external_id: string
  name: string | null
  organization_id: string
  organization_name: string
  phones_count: number
  webhook_subscribed: boolean
  created_at: string
}

interface OrgOption {
  id: string
  name: string
}

interface Pagination {
  page: number
  totalPages: number
  total: number
}

interface Props {
  wabas: WabaRow[]
  organizations: OrgOption[]
  pagination: Pagination
  search: string
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Index({ wabas, organizations, pagination, search }: Props) {
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedWaba, setSelectedWaba] = useState<WabaRow | null>(null)
  const [query, setQuery] = useState(search)
  const [subscribingId, setSubscribingId] = useState<string | null>(null)

  const createForm = useForm({ organization_id: '', external_id: '', name: '' })
  const editForm = useForm({ organization_id: '', external_id: '', name: '' })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/cc/wabas', { search: query }, { preserveState: true })
  }

  const goToPage = (page: number) => {
    router.get('/cc/wabas', { search, page }, { preserveState: true })
  }

  const orgOptions = organizations.map((org) => ({ label: org.name, value: org.id }))

  const handleCreate = () => {
    createForm.post('/cc/wabas', {
      onSuccess: () => {
        setCreateModalOpen(false)
        createForm.reset()
      },
    })
  }

  const handleEdit = (waba: WabaRow) => {
    setSelectedWaba(waba)
    editForm.setData({
      organization_id: waba.organization_id,
      external_id: waba.external_id,
      name: waba.name || '',
    })
    setEditModalOpen(true)
  }

  const handleUpdate = () => {
    if (!selectedWaba) return
    editForm.patch(`/cc/wabas/${selectedWaba.id}`, {
      onSuccess: () => {
        setEditModalOpen(false)
        setSelectedWaba(null)
      },
    })
  }

  const handleDeleteClick = (waba: WabaRow) => {
    setSelectedWaba(waba)
    setDeleteModalOpen(true)
  }

  const handleDelete = () => {
    if (!selectedWaba) return
    router.delete(`/cc/wabas/${selectedWaba.id}`, {
      onSuccess: () => {
        setDeleteModalOpen(false)
        setSelectedWaba(null)
      },
    })
  }

  const handleSubscribe = (wabaId: string) => {
    setSubscribingId(wabaId)
    router.post(`/cc/wabas/${wabaId}/subscribe_app`, {}, {
      onFinish: () => setSubscribingId(null),
    })
  }

  const hasNoWabasAtAll = wabas.length === 0 && !search
  const hasNoSearchResults = wabas.length === 0 && !!search

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-foreground">WABAs</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome, ID ou organização..."
                className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72"
              />
            </div>
            <Button type="submit" variant="outline" size="sm">
              Buscar
            </Button>
          </form>
          <Button size="sm" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Nova WABA
          </Button>
        </div>
      </div>

      {hasNoWabasAtAll ? (
        <EmptyState
          title="Nenhuma WABA cadastrada"
          description="Crie uma WABA para gerenciar números de telefone WhatsApp."
          image={<PhoneIcon className="w-12 h-12" />}
          action={{
            label: 'Nova WABA',
            onClick: () => setCreateModalOpen(true),
          }}
        />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">External ID</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Organização</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Telefones</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Webhook</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Criado em</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {wabas.map((waba) => (
                  <tr key={waba.id} className="hover:bg-accent transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        href={`/cc/wabas/${waba.id}`}
                        className="text-sm font-medium text-foreground hover:text-blue-600"
                      >
                        {waba.name || waba.external_id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{waba.external_id}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{waba.organization_name}</td>
                    <td className="px-4 py-3">
                      <Badge tone={waba.phones_count > 0 ? 'info' : undefined}>
                        {waba.phones_count}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {waba.webhook_subscribed ? (
                          <Badge tone="success">Conectado</Badge>
                        ) : (
                          <>
                            <Badge tone="warning">Pendente</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={subscribingId === waba.id}
                              onClick={() => handleSubscribe(waba.id)}
                            >
                              <RefreshCw className={`h-4 w-4 ${subscribingId === waba.id ? 'animate-spin' : ''}`} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(waba.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(waba)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(waba)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {hasNoSearchResults && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      Nenhuma WABA encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
              <span className="text-sm text-muted-foreground">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => goToPage(pagination.page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => goToPage(pagination.page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Nova WABA"
        primaryAction={{
          content: 'Criar',
          onAction: handleCreate,
          loading: createForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setCreateModalOpen(false),
        }}
      >
        <div className="space-y-4">
          <ComboBox
            label="Organização"
            options={orgOptions}
            value={createForm.data.organization_id}
            onChange={(value) => createForm.setData('organization_id', value)}
            placeholder="Selecione uma organização"
            error={createForm.errors.organization_id}
          />
          <TextField
            label="External ID"
            value={createForm.data.external_id}
            onChange={(value) => createForm.setData('external_id', value)}
            error={createForm.errors.external_id}
            helpText="ID da WABA no Meta Business"
          />
          <TextField
            label="Nome"
            value={createForm.data.name}
            onChange={(value) => createForm.setData('name', value)}
            error={createForm.errors.name}
            helpText="Opcional"
          />
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Editar WABA"
        primaryAction={{
          content: 'Salvar',
          onAction: handleUpdate,
          loading: editForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setEditModalOpen(false),
        }}
      >
        <div className="space-y-4">
          <ComboBox
            label="Organização"
            options={orgOptions}
            value={editForm.data.organization_id}
            onChange={(value) => editForm.setData('organization_id', value)}
            placeholder="Selecione uma organização"
            error={editForm.errors.organization_id}
          />
          <TextField
            label="External ID"
            value={editForm.data.external_id}
            onChange={(value) => editForm.setData('external_id', value)}
            error={editForm.errors.external_id}
          />
          <TextField
            label="Nome"
            value={editForm.data.name}
            onChange={(value) => editForm.setData('name', value)}
            error={editForm.errors.name}
          />
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Excluir WABA"
        destructiveAction={{
          content: 'Excluir',
          onAction: handleDelete,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setDeleteModalOpen(false),
        }}
      >
        <Text>
          Tem certeza que deseja excluir a WABA <strong>{selectedWaba?.name || selectedWaba?.external_id}</strong>?
        </Text>
        {selectedWaba && selectedWaba.phones_count > 0 && (
          <p className="mt-2 text-sm text-red-600">
            Esta ação irá excluir {selectedWaba.phones_count} telefone(s) associado(s).
          </p>
        )}
      </Modal>
    </div>
  )
}
